import React, { useEffect, useState, useCallback } from 'react';
import { FiMail, FiPhone, FiMapPin, FiChevronDown } from 'react-icons/fi';
import {
  FOOTER_BRAND,
  FOOTER_QUICK_LINKS,
  FOOTER_DESTINATIONS,
  FOOTER_SERVICES,
  FOOTER_CONTACT,
  FOOTER_SOCIALS,
  LEGAL_LINKS,
  BACK_TO_TOP_ICON,
} from './Footer.js';
import './Footer.css';

const Footer = () => {
  const [showTop, setShowTop] = useState(false);
  const [openColumns, setOpenColumns] = useState({});
  const BackToTopIcon = BACK_TO_TOP_ICON;

  const toggleColumn = useCallback((key) => {
    setOpenColumns((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  useEffect(() => {
    const onScroll = () => setShowTop(window.scrollY > 600);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  const currentYear = new Date().getFullYear();

  return (
    <footer className="ft">
      {/* World map decorative overlay */}
      <div className="ft__map-overlay" aria-hidden="true" />

      <div className="rt__container ft__top">
        {/* Brand Column */}
        <div className="ft__brand" data-aos="fade-up">
          <img src="/assets/logo.png" alt="OrbisDMC" className="ft__logo" />
          <p className="ft__desc">{FOOTER_BRAND.description}</p>
          <div className="ft__socials">
            {FOOTER_SOCIALS.map((social) => {
              const Icon = social.icon;
              return (
                <a
                  key={social.label}
                  href={social.path}
                  aria-label={social.label}
                  className="ft__social-link"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Icon />
                </a>
              );
            })}
          </div>
        </div>

        {/* Quick Links */}
        <div className={`ft__column${openColumns.quick ? ' ft__column--open' : ''}`}>
          <button className="ft__column-title" onClick={() => toggleColumn('quick')}>
            {FOOTER_QUICK_LINKS.title}
            <FiChevronDown className="ft__column-arrow" />
          </button>
          <div className={`ft__column-body${openColumns.quick ? ' ft__column-body--open' : ''}`}>
            <ul className="ft__links">
              {FOOTER_QUICK_LINKS.links.map((link) => (
                <li key={link.label}>
                  <a href={link.path} className="ft__link">{link.label}</a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Destinations */}
        <div className={`ft__column${openColumns.dest ? ' ft__column--open' : ''}`}>
          <button className="ft__column-title" onClick={() => toggleColumn('dest')}>
            {FOOTER_DESTINATIONS.title}
            <FiChevronDown className="ft__column-arrow" />
          </button>
          <div className={`ft__column-body${openColumns.dest ? ' ft__column-body--open' : ''}`}>
            <ul className="ft__links">
              {FOOTER_DESTINATIONS.links.map((link) => (
                <li key={link.label}>
                  <a href={link.path} className="ft__link">{link.label}</a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Services */}
        <div className={`ft__column${openColumns.serv ? ' ft__column--open' : ''}`}>
          <button className="ft__column-title" onClick={() => toggleColumn('serv')}>
            {FOOTER_SERVICES.title}
            <FiChevronDown className="ft__column-arrow" />
          </button>
          <div className={`ft__column-body${openColumns.serv ? ' ft__column-body--open' : ''}`}>
            <ul className="ft__links">
              {FOOTER_SERVICES.links.map((link) => (
                <li key={link.label}>
                  <a href={link.path} className="ft__link">{link.label}</a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Contact */}
        <div className={`ft__column ft__column--contact${openColumns.contact ? ' ft__column--open' : ''}`}>
          <button className="ft__column-title" onClick={() => toggleColumn('contact')}>
            {FOOTER_CONTACT.title}
            <FiChevronDown className="ft__column-arrow" />
          </button>
          <div className={`ft__column-body${openColumns.contact ? ' ft__column-body--open' : ''}`}>
            <div className="ft__contact-details">
              <div className="ft__contact-item">
                <FiMapPin className="ft__contact-icon" />
                <span>{FOOTER_CONTACT.address}</span>
              </div>
              <div className="ft__contact-item">
                <FiMail className="ft__contact-icon" />
                <a href={`mailto:${FOOTER_CONTACT.email}`} className="ft__contact-link">
                  {FOOTER_CONTACT.email}
                </a>
              </div>
              <div className="ft__contact-item">
                <FiPhone className="ft__contact-icon" />
                <a href={`tel:${FOOTER_CONTACT.phoneHref}`} className="ft__contact-link">
                  {FOOTER_CONTACT.phone}
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="ft__bottom">
        <div className="rt__container ft__bottom-inner">
          <div className="ft__copyright">
            &copy; {currentYear} {FOOTER_BRAND.name}. All rights reserved.
          </div>
          <div className="ft__legal">
            {LEGAL_LINKS.map((link) => (
              <a key={link.label} href={link.path} className="ft__legal-link">
                {link.label}
              </a>
            ))}
          </div>
        </div>
      </div>

      {/* Scroll to Top */}
      <button
        className={`ft__to-top ${showTop ? 'is-visible' : ''}`}
        onClick={scrollToTop}
        aria-label="Back to top"
      >
        <BackToTopIcon />
      </button>
    </footer>
  );
};

export default Footer;
